import { useMemo } from 'react';
import { useTheme } from '@/components/theme-provider';
import { Avatar } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/useUserStore';
import BoringAvatar from 'boring-avatars';
import {
  BadgeCheck,
  ChevronsUpDown,
  LogOut,
  Moon,
  Palette,
  ScrollText,
  Settings,
  Sparkles,
  Sun,
} from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import SlackLogo from './slack-logo';

type AvatarVariant = 'beam' | 'marble' | 'pixel' | 'sunset' | 'ring' | 'bauhaus';

interface AvatarConfig {
  seed: string;
  variant: AvatarVariant;
  colors: string[];
}

interface UserMetadata {
  full_name?: string | null;
  avatar_config?: string | AvatarConfig | null;
}

interface NavUserProps {
  user: {
    id: string;
    email?: string;
    user_metadata?: UserMetadata;
  };
  className?: string;
}

const avatarVariants: AvatarVariant[] = [
  'beam',
  'marble',
  'pixel',
  'sunset',
  'ring',
  'bauhaus',
];

const avatarColorPalettes: string[][] = [
  ['#6366F1', '#8B5CF6', '#EC4899', '#F97316', '#10B981'],
  ['#1E90FF', '#00BFFF', '#7C3AED', '#14B8A6', '#F59E0B'],
  ['#F87171', '#FB7185', '#FBBF24', '#34D399', '#60A5FA'],
  ['#8B5CF6', '#22D3EE', '#34D399', '#F97316', '#F472B6'],
  ['#636363', '#3E3A3A', '#8B5CF6', '#A855F7', '#EC4899'],
  ['#0EA5E9', '#22D3EE', '#F97316', '#FB7185', '#FACC15'],
];

const genConfig = ({ seed }: { seed: string }): AvatarConfig => {
  const randomVariant =
    avatarVariants[Math.floor(Math.random() * avatarVariants.length)];
  const randomColors =
    avatarColorPalettes[
      Math.floor(Math.random() * avatarColorPalettes.length)
    ];
  return {
    seed,
    variant: randomVariant,
    colors: randomColors,
  };
};

const parseAvatarConfig = (value: unknown): AvatarConfig | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as AvatarConfig;
    } catch (error) {
      console.warn('Failed to parse avatar config from metadata', error);
      return undefined;
    }
  }
  if (typeof value === 'object') {
    return value as AvatarConfig;
  }
  return undefined;
};

export function NavUser({ user, className }: NavUserProps) {
  const { isMobile } = useSidebar();
  const { clearUser } = useUserStore();
  const { setTheme } = useTheme();
  const navigate = useNavigate();

  const handleSignOut = async (): Promise<void> => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      clearUser();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const metadata: UserMetadata = user.user_metadata ?? {};
  const fullName = useMemo(() => {
    if (!metadata.full_name) return undefined;
    const trimmed = metadata.full_name.trim();
    return trimmed.length ? trimmed : undefined;
  }, [metadata.full_name]);
  const parsedAvatarConfig = useMemo(
    () => parseAvatarConfig(metadata.avatar_config),
    [metadata.avatar_config]
  );
  const fallbackSeed = useMemo(
    () => user.id || user.email || 'hooklistener',
    [user.id, user.email]
  );
  const avatarConfig = useMemo<AvatarConfig>(
    () => parsedAvatarConfig ?? genConfig({ seed: fallbackSeed }),
    [parsedAvatarConfig, fallbackSeed]
  );

  const getUsername = (email: string | undefined): string => {
    if (fullName) return fullName;
    if (!email) return '';
    return email.split('@')[0];
  };

  return (
    <SidebarMenu className={className}>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="group transition-colors hover:bg-white/5 data-[state=open]:bg-white/10"
            >
              <Avatar className="h-8 w-8 rounded-lg bg-linear-to-br from-purple-500/20 to-blue-500/20 ring-1 ring-white/10">
                <BoringAvatar
                  size={32}
                  name={avatarConfig.seed}
                  variant={avatarConfig.variant}
                  colors={avatarConfig.colors}
                />
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {getUsername(user.email)}
                </span>
                <span className="truncate text-xs text-muted-foreground">
                  {user.email}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto size-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg border border-white/10 bg-background/80 backdrop-blur-xs"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 p-2 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg bg-linear-to-br from-purple-500/20 to-blue-500/20 ring-1 ring-white/10">
                  <BoringAvatar
                    size={32}
                    name={avatarConfig.seed}
                    variant={avatarConfig.variant}
                    colors={avatarConfig.colors}
                  />
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">
                    {getUsername(user.email)}
                  </span>
                  <span className="truncate text-xs text-muted-foreground">
                    {user.email}
                  </span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuGroup>
              <DropdownMenuItem
                className="group"
                onClick={() => navigate({ to: '/organization/settings/billing' })}
              >
                <Sparkles className="mr-2 h-4 w-4 text-purple-500 group-hover:text-purple-400" />
                <span className="font-medium">Upgrade plan</span>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => navigate({ to: '/account' })}>
                <BadgeCheck className="mr-2 h-4 w-4" />
                Account
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate({ to: '/organization/settings' })}>
                <Settings className="mr-2 h-4 w-4" />
                Organization Settings
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuGroup>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Palette className="mr-2 h-4 w-4" />
                  Theme
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem onClick={() => setTheme('light')}>
                      <Sun className="mr-2 h-4 w-4" />
                      Light
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setTheme('dark')}>
                      <Moon className="mr-2 h-4 w-4" />
                      Dark
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setTheme('system')}>
                      <div className="mr-2 h-4 w-4 flex items-center justify-center">
                        <Sun className="h-3 w-3 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                        <Moon className="absolute h-3 w-3 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                      </div>
                      System
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            </DropdownMenuGroup>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <a
                  href="https://join.slack.com/t/hooklistenercommunity/shared_invite/zt-3208b4jyd-2wy2asb2RppM0D52ASBrlw"
                  target="_blank"
                  rel="noreferrer"
                  className="flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-hidden transition-colors focus:bg-accent focus:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50"
                >
                  <div className="mr-2 h-4 w-4 flex items-center justify-center">
                    <SlackLogo />
                  </div>
                  Slack
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a
                  href="https://hooklistener.com/changelog"
                  target="_blank"
                  rel="noreferrer"
                  className="flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-hidden transition-colors focus:bg-accent focus:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50"
                >
                  <ScrollText className="mr-2 h-4 w-4" />
                  Changelog
                </a>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem
              onClick={handleSignOut}
              className="text-red-500 focus:text-red-500"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
